import { useState, useRef, useEffect } from 'react';
import { Plus, Check, X } from 'lucide-react';

interface NewPropertyPlaceholderProps {
  onCreateProperty: (name: string) => void;
}

export function NewPropertyPlaceholder({ onCreateProperty }: NewPropertyPlaceholderProps) {
  const [isActive, setIsActive] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isActive]);

  const handleClick = () => {
    setIsActive(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && propertyName.trim()) {
      onCreateProperty(propertyName.trim());
      setPropertyName('');
      setIsActive(false);
    } else if (e.key === 'Escape') {
      setPropertyName('');
      setIsActive(false);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPropertyName('');
    setIsActive(false);
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (propertyName.trim()) {
      onCreateProperty(propertyName.trim());
      setPropertyName('');
      setIsActive(false);
    }
  };

  if (isActive) {
    return (
      <div className="group relative bg-card/50 rounded-[24px] border-2 border-dashed border-primary/50 flex flex-col h-[320px] w-full p-6 items-center justify-center transition-all duration-300">
        <div className="w-full max-w-[240px] flex flex-col gap-4">
          <div className="text-center">
            <h3 className="text-[18px] font-semibold text-foreground mb-1">Nowa nieruchomość</h3>
            <p className="text-[13px] text-muted-foreground">Wprowadź nazwę projektu</p>
          </div>

          <input
            ref={inputRef}
            type="text"
            value={propertyName}
            onChange={(e) => setPropertyName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Np. Apartament Wilanów"
            className="w-full h-10 px-3 rounded-lg border border-border bg-background text-[14px] focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50 text-center"
          />

          <div className="flex items-center justify-center gap-2 mt-2">
            <button
              onClick={handleCancel}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
              title="Anuluj"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              disabled={!propertyName.trim()}
            >
              <Plus className="w-4 h-4" />
              Utwórz
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="group relative bg-card/30 hover:bg-card/50 rounded-[24px] border-2 border-dashed border-border hover:border-primary/50 flex flex-col h-[320px] w-full items-center justify-center cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
    >
      <div className="w-16 h-16 rounded-full bg-background shadow-md flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-4 border border-border/50">
        <Plus className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
      </div>

      <div className="text-center">
        <h3 className="text-[18px] font-semibold text-muted-foreground group-hover:text-foreground transition-colors duration-300">
          Dodaj nieruchomość
        </h3>
        <p className="text-[13px] text-muted-foreground/70 mt-1">
          Kliknij aby utworzyć nowy folder
        </p>
      </div>
    </button>
  );
}